import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, MessageSquare, Plus, Repeat, Settings, LogOut, ShieldCheck, Search, ChevronDown, User as UserIcon, Radar } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, imgSrc } from "@/lib/api";

/**
 * Marketplace top nav.
 * Bold wordmark, integrated search, dense action icons.
 */
function Logo({ size = "text-[26px]" }) {
  return (
    <Link to="/" className={`font-display font-bold ${size} tracking-tight text-slate-900 flex items-center gap-1 leading-none`} data-testid="logo-link">
      Vyrti
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F5AF02] ml-0.5 mt-2" />
    </Link>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread_count");
        setUnread(data.count || 0);
      } catch (e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const onClickAway = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/browse?q=${encodeURIComponent(q.trim())}` : "/browse");
  };

  const navLink = ({ isActive }) =>
    `text-[13px] font-semibold px-3 py-2 transition-colors ${isActive ? "text-[#0654BA]" : "text-slate-700 hover:text-[#0654BA]"}`;

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
      <div className="section-pad h-14 md:h-16 flex items-center gap-4 md:gap-6">
        <Logo />

        {/* Integrated search — desktop only */}
        <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl mx-2" data-testid="navbar-search-form">
          <div className="flex flex-1 items-stretch border border-slate-300 rounded-md overflow-hidden focus-within:border-[#0654BA] focus-within:ring-2 focus-within:ring-[#0654BA]/15 transition">
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search trades — cameras, jackets, vinyl…"
              className="flex-1 px-3.5 py-2 text-sm outline-none bg-white placeholder:text-slate-400"
              data-testid="navbar-search-input"
            />
            <button type="submit" className="px-4 bg-[#0654BA] hover:bg-[#0445A0] text-white flex items-center gap-1.5 text-sm font-semibold transition-colors" data-testid="navbar-search-submit">
              <Search className="w-4 h-4" />
              <span className="hidden lg:inline">Search</span>
            </button>
          </div>
        </form>

        {/* Right side actions */}
        <div className="ml-auto flex items-center gap-1 md:gap-2">
          <nav className="hidden lg:flex items-center mr-2">
            <NavLink to="/browse" className={navLink} data-testid="nav-browse">Browse</NavLink>
            <NavLink to="/what-can-i-get" className={navLink} data-testid="nav-what-can-i-get">What Can I Get?</NavLink>
            <NavLink to="/categories" className={navLink} data-testid="nav-categories">Categories</NavLink>
            <NavLink to="/safety" className={navLink} data-testid="nav-safety">Safety</NavLink>
          </nav>

          {!user ? (
            <>
              <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-[#0654BA] px-3 py-2" data-testid="nav-login">Sign in</Link>
              <Link to="/signup" className="btn-block btn-accent !text-sm !py-2 !px-4" data-testid="nav-signup">Join free</Link>
            </>
          ) : (
            <>
              <Link to="/notifications" className="relative text-slate-600 hover:text-[#0654BA] p-2 rounded-md hover:bg-slate-100 transition-colors" data-testid="nav-notifications">
                <Bell className="w-[18px] h-[18px]" strokeWidth={2} />
                {unread > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#F5AF02]" data-testid="unread-dot" />
                )}
              </Link>
              <Link to="/messages" className="text-slate-600 hover:text-[#0654BA] p-2 rounded-md hover:bg-slate-100 transition-colors" data-testid="nav-messages">
                <MessageSquare className="w-[18px] h-[18px]" strokeWidth={2} />
              </Link>
              <Link to="/trades" className="hidden md:inline-flex text-slate-600 hover:text-[#0654BA] p-2 rounded-md hover:bg-slate-100 transition-colors" data-testid="nav-trades">
                <Repeat className="w-[18px] h-[18px]" strokeWidth={2} />
              </Link>
              <Link to="/post" className="btn-block btn-accent !text-sm !py-2 !px-3 md:!px-4" data-testid="nav-post-trade">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Post trade</span>
              </Link>
              <div className="relative ml-1" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(o => !o)}
                  className="flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-slate-400 transition p-0.5 pr-2"
                  data-testid="user-menu-trigger"
                  aria-label="Account menu"
                >
                  {user.picture ? (
                    <img src={imgSrc(user.picture)} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-700 text-xs">{user.name?.[0]?.toUpperCase()}</div>
                  )}
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-md shadow-lg py-1.5 z-50" data-testid="user-menu">
                    <div className="px-3 py-2 border-b border-slate-100 mb-1">
                      <p className="text-sm font-semibold text-slate-900">{user.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                    <Link to="/me" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="menu-profile">
                      <UserIcon className="w-4 h-4 text-slate-500" /> My profile
                    </Link>
                    <Link to="/what-can-i-get" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="menu-match-radar">
                      <Radar className="w-4 h-4 text-[#0654BA]" /> My Match Radar
                    </Link>
                    <Link to="/trades" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="menu-trades">
                      <Repeat className="w-4 h-4 text-slate-500" /> My trades
                    </Link>
                    <Link to="/me/edit" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="menu-settings">
                      <Settings className="w-4 h-4 text-slate-500" /> Settings
                    </Link>
                    {user.is_admin && (
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-[#0654BA] hover:bg-slate-50 font-semibold border-t border-slate-100 mt-1 pt-2" data-testid="menu-admin">
                        <ShieldCheck className="w-4 h-4" /> Admin console
                      </Link>
                    )}
                    <button onClick={() => { logout(); setUserMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 border-t border-slate-100 mt-1" data-testid="menu-logout">
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile search row */}
      {!!user && (
        <form onSubmit={handleSearch} className="md:hidden section-pad pb-2.5 -mt-1" data-testid="mobile-search-form">
          <div className="flex items-stretch border border-slate-300 rounded-md overflow-hidden focus-within:border-[#0654BA] focus-within:ring-2 focus-within:ring-[#0654BA]/15">
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search trades…"
              className="flex-1 px-3 py-2 text-sm outline-none bg-white placeholder:text-slate-400"
            />
            <button type="submit" className="px-3 bg-[#0654BA] text-white">
              <Search className="w-4 h-4" />
            </button>
          </div>
        </form>
      )}
    </header>
  );
}
